import React, { useState } from "react"
import "react-phone-number-input/style.css"
import { getCountries } from "react-phone-number-input/input"
import { useIntl } from "gatsby-plugin-intl"
import { useForm } from "react-hook-form"
import PropTypes from "prop-types"
import en from "react-phone-number-input/locale/en.json"
import examples from "libphonenumber-js/examples.mobile.json"
import { getExampleNumber } from "libphonenumber-js"

import patterns from "utils/patterns"
import CountryFlagDropdown from "components/atoms/CountryFlagDropdown"

import {
  StyledForm,
  StyledField,
  StyledInput,
  StyledLabel,
  StyledTextarea,
  StyledButton,
  StyledError,
  StyledPhoneInput,
  StyledPhoneInputCountry,
  StyledCountrySelect,
  StyledButtonWrapper,
} from "./ContactForm.styles"

const ContactForm = ({
  sendForm,
  phoneError,
  type,
  onCancel,
  asPage,
  trackingOptions,
}) => {
  const { formatMessage } = useIntl()
  const [country, setCountry] = useState("US")
  const { register, handleSubmit, errors, control, getValues } = useForm()

  const handleCountryChange = () => setCountry(getValues("country"))

  const emailValidationError = (errorType) => {
    const errorMsg = {
      required: formatMessage({ id: "contactForm.error.emailRequired" }),
      pattern: formatMessage({ id: "contactForm.error.emailPattern" }),
    }
    return errorMsg[errorType]
  }

  return (
    <StyledForm
      onSubmit={handleSubmit(sendForm)}
      type={type}
      data-testid="contactForm"
    >
      <StyledField>
        <StyledInput
          ref={register({ required: true })}
          name="name"
          id="nameInput"
          placeholder=" "
          iserror={errors.name}
          data-testid="contactNameInput"
        />
        <StyledLabel htmlFor="nameInput" data-testid="contactNameLabel">
          {formatMessage({ id: "contactForm.label.name" })}
        </StyledLabel>
        {errors.name && (
          <StyledError data-testid="contactNameError">
            {formatMessage({ id: "contactForm.error.nameRequired" })}
          </StyledError>
        )}
      </StyledField>
      <StyledField>
        <StyledInput
          ref={register({
            required: true,
            pattern: patterns.email,
          })}
          name="email"
          placeholder=" "
          id="emailInput"
          iserror={errors.email}
          data-testid="contactEmailInput"
        />
        <StyledLabel htmlFor="emailInput" data-testid="contactEmailLabel">
          {formatMessage({ id: "contactForm.label.email" })}
        </StyledLabel>

        {errors.email && (
          <StyledError data-testid="contactEmailError">
            {emailValidationError(errors.email.type)}
          </StyledError>
        )}
      </StyledField>
      <StyledField>
        <StyledPhoneInputCountry>
          <StyledCountrySelect
            defaultValue="US"
            ref={register}
            name="country"
            onChange={handleCountryChange}
            data-testid="contactCountrySelect"
          >
            {getCountries().map((countryCode) => (
              <option
                data-testid="contactCountrySelectOption"
                key={countryCode}
                value={countryCode}
              >
                {en[countryCode]}
              </option>
            ))}
          </StyledCountrySelect>
          <CountryFlagDropdown control={control} />
        </StyledPhoneInputCountry>
        <StyledPhoneInput
          ref={register}
          id="phoneInput"
          name="phone"
          iserror={errors.phone}
          placeholder={getExampleNumber(country, examples).formatNational()}
          data-testid="contactPhoneInput"
        />
        <StyledLabel htmlFor="phoneInput" data-testid="contactPhoneLabel">
          {formatMessage({ id: "contactForm.label.phone" })}
        </StyledLabel>
        {phoneError && (
          <StyledError data-testid="contactPhoneError">
            {phoneError}
          </StyledError>
        )}
      </StyledField>

      <StyledField>
        <StyledTextarea
          rows="4"
          ref={register({ required: true })}
          id="messageInput"
          name="message"
          placeholder=" "
          iserror={errors.message}
          data-testid="contactMessageInput"
        />
        <StyledLabel htmlFor="messageInput" data-testid="contactMessageLabel">
          {formatMessage({ id: "contactForm.label.message" })}
        </StyledLabel>
        {errors.message && (
          <StyledError data-testid="contactMessageError">
            {formatMessage({ id: "contactForm.error.messageRequired" })}
          </StyledError>
        )}
      </StyledField>
      <StyledButtonWrapper asPage={asPage}>
        {!asPage && type === "secondary" && (
          <StyledButton
            type="secondary"
            onClick={onCancel}
            data-testid="contactFormCancel"
          >
            {formatMessage({ id: "contactForm.cancelButton" })}
          </StyledButton>
        )}
        <StyledButton
          type="submit"
          data-testid="contactFormSubmit"
          trackingOptions={trackingOptions}
        >
          {formatMessage({ id: "contactForm.submitButton" })}
        </StyledButton>
      </StyledButtonWrapper>
    </StyledForm>
  )
}

export default ContactForm

ContactForm.propTypes = {
  sendForm: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  phoneError: PropTypes.string,
  type: PropTypes.string,
  asPage: PropTypes.bool,
  trackingOptions: PropTypes.shape(),
}

ContactForm.defaultProps = {
  phoneError: null,
  onCancel: () => null,
  type: "primary",
  asPage: false,
  trackingOptions: null,
}
